import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { WorkflowRuleFullPage } from "@/components/Organisms/WorkflowRuleFullPage/WorkflowRuleFullPage";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { getAssetTypeDisplayName } from "@/utils/asset-type-detail/asset-type-detail.utils";
import { AssetTypeDrawerContext } from "@/utils/asset-type-detail/asset-type-drawer-context";

const SearchSchema = z.object({
	ruleTab: z.enum(["details", "activity"]).optional().default("details"),
});

const ROUTE_ID =
	"/_authenticated/(admin-app)/_admin/admin/configuration/assetTypes/$assetTypeId/full/rules/$ruleId" as const;

export const Route = createFileRoute(ROUTE_ID)({
	component: AdminAssetTypeRuleDetailPage,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminAssetTypeRuleDetailPage() {
	const ctx = useContext(AssetTypeDrawerContext);
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ from: ROUTE_ID });
	const search = useSearch({ from: ROUTE_ID });

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canDeleteRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canDeleteRule = Boolean(canDeleteRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	if (!ctx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);
	}

	const { assetType } = ctx;

	return (
		<WorkflowRuleFullPage
			ruleId={params.ruleId}
			organizationId={EMPTY_ORG_ID}
			entityTypeId={params.assetTypeId}
			mode="platform"
			canEdit={canEditRule}
			canDelete={canDeleteRule}
			canArchive={canArchiveRule}
			lifecycleId={assetType.statusLifecycle?.id ?? null}
			entityTypeLabel={t("admin.assetTypes.table.title")}
			entityTypeName={getAssetTypeDisplayName(assetType)}
			activeTab={search.ruleTab}
			onTabChange={(ruleTab) => {
				void navigate({
					to: "/admin/configuration/assetTypes/$assetTypeId/full/rules/$ruleId",
					params: { assetTypeId: params.assetTypeId, ruleId: params.ruleId },
					search: { tab: "rules", mode: "view", presentation: "full", ruleTab },
				});
			}}
			onBackToRules={() => {
				void navigate({
					to: "/admin/configuration/assetTypes/$assetTypeId/full",
					params: { assetTypeId: params.assetTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onNavigateToEntityTypeList={() => {
				void navigate({ to: "/admin/configuration/assetTypes" });
			}}
			onNavigateToEntityTypeRules={() => {
				void navigate({
					to: "/admin/configuration/assetTypes/$assetTypeId/full",
					params: { assetTypeId: params.assetTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onAfterAction={() => {
				void navigate({
					to: "/admin/configuration/assetTypes/$assetTypeId/full",
					params: { assetTypeId: params.assetTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
		/>
	);
}
